import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, PenLine, Eraser, FileText, ShieldCheck } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const SignDocument = ({ assignment, onClose, onSigned }) => {
  const [form, setForm] = useState(null);
  const [typed, setTyped] = useState("");
  const [consent, setConsent] = useState(false);
  const [release, setRelease] = useState("");
  const [busy, setBusy] = useState(false);
  const [drawn, setDrawn] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  // The dialog keeps rendering for a frame after the parent clears `assignment`, so never read
  // straight off it — fall back to the id captured with the form.
  const aid = assignment?.id || form?.assignment_id;

  useEffect(() => {
    if (!assignment) { setForm(null); return; }
    setTyped(""); setConsent(false); setDrawn(false); setRelease("");
    api.get(`/paperwork-assignments/${assignment.id}/sign-form`)
      .then(({ data }) => { setForm({ ...data, assignment_id: assignment.id }); setTyped(data.typed_name_suggestion || ""); })
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); closeRef.current?.(); });
  }, [assignment]);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 2.4; ctx.lineCap = "round"; ctx.strokeStyle = "#18181b";
    const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke();
    setDrawn(true);
  };
  const end = () => { drawing.current = false; };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setDrawn(false);
  };

  const sign = async () => {
    if (!drawn) return toast.error("Draw your signature in the box first");
    if (form.release_choice_required && !release) return toast.error("Choose Consent or Decline for the Name & Likeness release");
    setBusy(true);
    try {
      const { data } = await api.post(`/paperwork-assignments/${aid}/sign`, {
        typed_name: typed, consent, signature_image: canvasRef.current.toDataURL("image/png"),
        release_choice: release || undefined,
      });
      toast.success(`Signed — verification ${data.signature_id}. Sent to your coordinator for review.`);
      onClose();
      try { await onSigned?.(); } catch { /* the list refreshes on the next load */ }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!assignment} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto cc-scroll" data-testid="sign-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><PenLine className="w-4 h-4 text-cc-blue" />{form?.name || assignment?.name}</DialogTitle></DialogHeader>
        {!form ? <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cc-blue" /></div> : (
          <div className="space-y-4">
            {form.has_pdf && (
              <a href={`${BACKEND}/api/paperwork-assignments/${aid}/template`} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-cc-blue hover:underline" data-testid="sign-read-pdf">
                <FileText className="w-4 h-4" />Read the full document ({form.template_file_name})
              </a>
            )}
            {form.body_text && (
              <div className="max-h-56 overflow-y-auto cc-scroll border rounded-md p-4 bg-zinc-50/60 text-[13px] text-zinc-700 whitespace-pre-line leading-relaxed" data-testid="sign-body-text">
                {form.body_text}
              </div>
            )}
            {form.release_choice_required && (
              <div className="border rounded-md p-3 space-y-2" data-testid="sign-release-choice">
                <p className="text-xs uppercase tracking-wide text-zinc-400 font-semibold">Release for Use of Name and Likeness — choose one</p>
                {Object.entries(form.release_choices || {}).map(([key, text]) => (
                  <label key={key} className={`flex items-start gap-2 text-[13px] rounded-md p-2 cursor-pointer border ${release === key ? "border-cc-blue bg-blue-50/60 text-zinc-800" : "border-transparent text-zinc-600 hover:bg-zinc-50"}`}>
                    <input type="radio" name="release_choice" value={key} checked={release === key} onChange={() => setRelease(key)}
                      className="mt-1 accent-[#0b5cab]" data-testid={`sign-release-${key}`} />
                    <span>{text}</span>
                  </label>
                ))}
                {form.facility_name && <p className="text-[11px] text-zinc-400">TMC Facility Name on the form: <span className="text-zinc-600">{form.facility_name}</span></p>}
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-wide text-zinc-400 font-semibold">Your full legal name</label>
              <Input value={typed} onChange={(e) => setTyped(e.target.value)} className="mt-1" data-testid="sign-typed-name" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs uppercase tracking-wide text-zinc-400 font-semibold">Draw your signature</label>
                <button type="button" onClick={clear} className="text-xs text-zinc-500 inline-flex items-center gap-1 hover:text-red-600" data-testid="sign-clear">
                  <Eraser className="w-3.5 h-3.5" />Clear
                </button>
              </div>
              <canvas ref={canvasRef} width={640} height={160} data-testid="sign-canvas"
                onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                className="mt-1 w-full h-[160px] border-2 border-dashed border-zinc-300 rounded-md bg-white touch-none cursor-crosshair" />
            </div>
            <label className="flex items-start gap-2 text-[13px] text-zinc-600">
              <Checkbox checked={consent} onCheckedChange={(c) => setConsent(!!c)} data-testid="sign-consent" className="mt-0.5" />
              <span>{form.consent_text}</span>
            </label>
            <p className="text-[11px] text-zinc-400 flex items-start gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 mt-px shrink-0" />
              We record the date, time, your account and device with your signature so the document can be verified later.
            </p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={sign} disabled={busy || !form || !consent || !drawn || typed.trim().length < 3 || (form?.release_choice_required && !release)}
            className="bg-cc-blue text-white" data-testid="sign-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign and submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
